import { View, Text, Pressable, ScrollView, Switch, Platform } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import {
  ChevronLeft,
  User,
  Users,
  Bell,
  Shield,
  CreditCard,
  HelpCircle,
  LogOut,
  ChevronDown,
  ChevronUp,
  Moon,
  Globe,
  Mail,
  Smartphone,
  Download,
  Trash2,
  SlidersHorizontal,
  ExternalLink,
  Gift,
  TrendingUp,
  Code,
  BarChart3,
} from 'lucide-react-native';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { useThemeStore } from '@/lib/state/theme-store';
import { isWeb } from '@/lib/platform';
import { usePhoneShell } from '@/lib/useShell';
import { AppLayout, Breadcrumb } from '@/components/AppLayout';
import { useAuth } from '@/lib/supabase';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAlert } from '@/components/AlertDialog';
import { formatBuildLine } from '@/lib/feedback/device-info';
import { useSubscriptionStore } from '@/lib/state/subscription-store';
import { useInverioStore } from '@/lib/state/inverio-store';
import { useProfileStore } from '@/lib/state/profile-store';
import { TeamManagement } from '@/components/team';
import { ReferralCard } from '@/components/subscription';
import { useReferralStore } from '@/lib/referral';
import { PLANS_LINK, REFERRAL_LINK, openPlans } from '@/lib/commerce/navigation';
import { COPY } from '@/lib/commerce/copy';
import { track, useTrackScreen } from '@/lib/analytics';
import AnimatedPressable from '@/components/AnimatedPressable';
import { SettingsItem } from '@/components/settings/SettingsItem';
import { handleError, reportError } from '@/lib/errors';
import DevModeToggle from '@/components/DevModeToggle';
import { APP_MODE, modeDescriptions, isDemoMode } from '@/lib/config/app-mode';
import ApiKeySettings from '@/components/investor/ApiKeySettings';
import {
  DataExportError,
  deliverExport,
  isStillDeliverable,
  requestDataExport,
  type DataExport,
} from '@/lib/gdpr/deliver-export';
import { deletionBackendAvailable } from '@/lib/gdpr/account-deletion';
import { ManageDataError, useSetAnalyticsOptOut } from '@/lib/gdpr/manage-data';
import { StepUpModal } from '@/components/account/StepUpModal';

export default function SettingsScreen() {
  useTrackScreen('Settings');
  const settingsTracked = useRef(false);
  useEffect(() => {
    if (settingsTracked.current) return;
    settingsTracked.current = true;
    track('settings_opened');
  }, []);
  const router = useRouter();
  const { signOut, user, profile } = useAuth();
  const colors = useThemeColors();
  const phoneShell = usePhoneShell();
  const isDark = useThemeStore((s) => s.isDark);
  const toggleDarkMode = useThemeStore((s) => s.toggleDarkMode);
  // Usage analytics is an ACCOUNT setting (profiles.analytics_opt_out, 118-08):
  // this switch and Manage my data's are the same fact, changed through the same
  // mutation, which also drives the tracker's consent gate.
  const analyticsOptOut = useSetAnalyticsOptOut(user?.id);
  const accountOptOut = profile?.id === user?.id ? profile?.analytics_opt_out : undefined;
  // A step-up interrupted change, replayed once the code is verified.
  const [analyticsStepUp, setAnalyticsStepUp] = useState<boolean | null>(null);
  const { alert } = useAlert();
  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    marketing: false,
  });
  const [showTeamManagement, setShowTeamManagement] = useState(false);
  const [showReferralProgram, setShowReferralProgram] = useState(false);
  // The last export link, so tapping again while it is still valid re-delivers
  // the same file instead of spending the once-per-24h export.
  const lastExport = useRef<DataExport | null>(null);

  // Dev menu unlock: visible when __DEV__ or unlocked via 7-tap on version text
  const [devMenuUnlocked, setDevMenuUnlocked] = useState(__DEV__);
  const buildLine = useMemo(() => formatBuildLine(), []);
  const versionTapCount = useRef(0);
  const versionTapTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load persisted dev unlock state on mount
  useEffect(() => {
    if (__DEV__) return; // Already unlocked in dev
    (async () => {
      try {
        if (Platform.OS === 'web') {
          const val = typeof window !== 'undefined' && window.localStorage?.getItem('inverio_dev_menu_unlocked');
          if (val === 'true') setDevMenuUnlocked(true);
        } else {
          const SecureStore = require('expo-secure-store');
          const val = SecureStore.getItem?.('inverio_dev_menu_unlocked');
          if (val === 'true') setDevMenuUnlocked(true);
        }
      } catch (e) {
        reportError(e, { context: 'Settings.loadDevUnlock', level: 'warning', silent: true });
      }
    })();
  }, []);

  const handleVersionTap = useCallback(() => {
    if (devMenuUnlocked) return;
    versionTapCount.current += 1;

    if (versionTapTimer.current) clearTimeout(versionTapTimer.current);
    versionTapTimer.current = setTimeout(() => { versionTapCount.current = 0; }, 2000);

    if (versionTapCount.current >= 7) {
      versionTapCount.current = 0;
      setDevMenuUnlocked(true);
      // Persist unlock
      (async () => {
        try {
          if (Platform.OS === 'web') {
            window.localStorage?.setItem('inverio_dev_menu_unlocked', 'true');
          } else {
            const SecureStore = require('expo-secure-store');
            await SecureStore.setItemAsync('inverio_dev_menu_unlocked', 'true');
          }
        } catch (e) {
          reportError(e, { context: 'Settings.persistDevUnlock', level: 'warning', silent: true });
        }
      })();
      alert('Developer Mode', 'Developer options are now visible.');
    }
  }, [devMenuUnlocked, alert]);

  // Get subscription tier for team seats (startups only - investors are individuals)
  const startupTier = useSubscriptionStore((s) => s.getStartupTier());
  const subscriptionUserRole = useSubscriptionStore((s) => s.userRole);
  const setSubscriptionUserRole = useSubscriptionStore((s) => s.setUserRole);

  // Get userRole from main store (source of truth)
  const inverioUserRole = useProfileStore((s) => s.userRole);
  // Display name — the store user's name is the SSOT (resolved from
  // profiles.first_name/last_name at login), not raw user_metadata.
  const profileDisplayName = useProfileStore((s) => s.user?.name);

  // Sync userRole from inverio store to subscription store
  useEffect(() => {
    if (
      inverioUserRole &&
      inverioUserRole !== 'pending' &&
      inverioUserRole !== subscriptionUserRole
    ) {
      setSubscriptionUserRole(inverioUserRole);
    }
  }, [inverioUserRole, subscriptionUserRole, setSubscriptionUserRole]);

  // Use inverio store as source of truth, fallback to subscription store
  const userRole = inverioUserRole || subscriptionUserRole;

  // Get referral stats
  const getStats = useReferralStore((s) => s.getStats);
  const referralStats = getStats();

  // Dynamic breadcrumbs based on user role
  const breadcrumbs: Breadcrumb[] = userRole === 'startup'
    ? [
        { label: 'Profile', href: '/(startup)/userprofile' },
        { label: 'Settings' },
      ]
    : [
        { label: 'Profile', href: '/(investor)/investorprofile' },
        { label: 'Settings' },
      ];

  // Determine if user has team seats (startups with paid tiers only)
  // Note: Investors are individual users, no team management (institutional/VC firms would be separate enterprise tier)
  const hasPaidTier = userRole === 'startup' && startupTier !== 'free';
  const currentTier = startupTier || 'free';
  // Local alias so TS narrows the `| null` away inside the JSX ternary below —
  // narrowing a closed-over `const` works within a function scope; it does not
  // reliably carry through a directly-referenced cross-module import.
  const goToPlans = openPlans;

  // Get store reset functions
  const resetInverioStore = useInverioStore((s) => s.reset);
  const resetSubscriptionStore = useSubscriptionStore((s) => s.reset);
  const resetProfileStore = useProfileStore((s) => s.reset);

  const handleSignOut = async () => {
    try {
      // Sign out from Supabase (resets stores, clears demo bypass, clears auth state)
      await signOut();
      // Belt-and-suspenders store reset
      resetInverioStore();
      resetSubscriptionStore();
      resetProfileStore();
    } catch (error) {
      reportError(error, { context: 'Settings.handleSignOut' });
    } finally {
      // Navigate to login after sign-out (even if it threw)
      if (isWeb) {
        window.location.replace('/login');
      } else {
        router.replace('/login');
      }
    }
  };

  const showComingSoon = (feature: string) => {
    alert('Coming Soon', `${feature} will be available in a future update.`);
  };

  // Art. 15/20 export: the server stores the ZIP privately and returns a
  // short-lived signed URL; deliverExport() downloads it (web) or saves it via
  // the share sheet (native). See src/lib/gdpr/deliver-export.ts.
  const exportMutation = useMutation({
    mutationFn: async () => {
      const reused = isStillDeliverable(lastExport.current);
      const result = reused && lastExport.current ? lastExport.current : await requestDataExport();
      lastExport.current = result;
      const outcome = await deliverExport(result);
      return { result, outcome, reused };
    },
    onSuccess: ({ result, outcome, reused }) => {
      if (outcome.status === 'cancelled') return;
      if (outcome.status === 'failed') {
        alert('Export Not Saved', outcome.message);
        return;
      }
      if (!reused) track('data_exported', { size_bytes: result.bytes });
      const destination = isWeb ? 'Your download has started' : 'Your export was handed to the share sheet';
      const partialNote = result.partial
        ? ' Some items could not be included; data.json lists exactly which.'
        : '';
      alert(
        'Export Ready',
        `${destination} (${formatExportSize(result.bytes)}). It is a ZIP of all your Inverio data as JSON, plus your documents.${partialNote}`,
      );
    },
    onError: (err) => {
      const { message, isCancelled } = handleError(err, { context: 'Settings.handleExportData' });
      if (isCancelled) return;
      alert('Export Failed', err instanceof DataExportError ? err.userMessage : message);
    },
  });
  const isExporting = exportMutation.isPending;

  const handleExportData = () => {
    if (exportMutation.isPending) return;
    exportMutation.mutate();
  };

  // Deletion is its own screen (118-07): a server preview of what happens, a
  // typed confirmation, the MFA step-up and a 30-day grace period. A shared
  // demo account cannot be deleted (the server refuses it too), and offline
  // sample mode has no account to delete — so the row is informational there.
  const deletionAvailable = deletionBackendAvailable() && !isDemoMode();

  const changeAnalytics = (enabled: boolean) => {
    if (analyticsOptOut.isPending) return;
    analyticsOptOut.mutate(!enabled, {
      onError: (err) => {
        if (err instanceof ManageDataError && err.kind === 'step_up_required') {
          setAnalyticsStepUp(!enabled);
          return;
        }
        const { message, isCancelled } = handleError(err, { context: 'Settings.usageAnalytics' });
        if (!isCancelled) alert('Not saved', err instanceof ManageDataError ? err.userMessage : message);
      },
    });
  };
  // The same availability as Manage my data: the server refuses the preference
  // for a shared demo account, and sample mode has no account to save it on.
  const analyticsDescription = !deletionAvailable
    ? isDemoMode()
      ? 'Not available on shared demo accounts'
      : 'Not available in offline sample mode'
    : 'Share which screens and features you use, to help us improve Inverio. Saved to your account, on every device.';

  return (
    <AppLayout title="Settings" breadcrumbs={breadcrumbs}>
      <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
        <SafeAreaView edges={isWeb ? [] : ['top']} className="flex-1">
          <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
            {/* Header - Mobile only */}
            {phoneShell && (
              <Animated.View
                entering={FadeInDown.delay(100).duration(500)}
                className="flex-row items-center gap-4 px-5 pt-4 pb-6"
              >
                <AnimatedPressable
                  onPress={() => router.back()}
                  accessibilityRole="button"
                  accessibilityLabel="Go back"
                  className="w-10 h-10 rounded-full items-center justify-center"
                  style={{ backgroundColor: colors.surface.primary }}
                >
                  <ChevronLeft size={24} color={colors.text.primary} />
                </AnimatedPressable>
                <Text style={{ color: colors.text.primary }} className="text-2xl font-bold">
                  Settings
                </Text>
              </Animated.View>
            )}

            <View className={`px-5 pb-8 ${isWeb ? 'pt-6' : ''}`}>
              {/* Account Section */}
              <SettingsSection title="Account">
                <SettingsItem
                  icon={<User size={20} color={colors.primary[600]} />}
                  label="Edit Profile"
                  description={userRole === 'startup' ? 'Company & personal info' : 'Investment preferences'}
                  onPress={() => {
                    if (userRole === 'startup') {
                      router.push('/(startup)/company');
                    } else {
                      router.push('/(investor)/investorprofile');
                    }
                  }}
                />
                <SettingsItem
                  icon={<Shield size={20} color={colors.primary[600]} />}
                  label="Privacy & Security"
                  description="2FA, password, security settings"
                  onPress={() => router.push('/security')}
                />
                {PLANS_LINK && (
                  <SettingsItem
                    icon={<CreditCard size={20} color={colors.primary[600]} />}
                    label={PLANS_LINK.label}
                    onPress={() => PLANS_LINK!.open(router, userRole === 'startup' ? 'startup' : 'investor')}
                  />
                )}
                {/* Team Management - Startups only (investors are individual users) */}
                {userRole === 'startup' && (
                  <SettingsItem
                    icon={<Users size={20} color={colors.primary[600]} />}
                    label="Team Management"
                    description={hasPaidTier ? 'Manage team members' : COPY.teamLockedHint}
                    onPress={
                      hasPaidTier
                        ? () => setShowTeamManagement(!showTeamManagement)
                        : goToPlans
                          ? () => goToPlans(router, 'startup')
                          : undefined
                    }
                    badge={hasPaidTier ? undefined : COPY.teamLockedBadge}
                    expanded={showTeamManagement && hasPaidTier}
                  />
                )}
              </SettingsSection>

              {/* Team Management (expanded) - Startups only */}
              {userRole === 'startup' && showTeamManagement && hasPaidTier && (
                <Animated.View entering={FadeInDown.duration(400)} className="mb-6 -mt-3">
                  <TeamManagement
                    tier={currentTier as 'free' | 'starter' | 'standard' | 'premium'}
                    userId={user?.id || 'user-123'}
                    userName={profileDisplayName || 'User'}
                    userEmail={user?.email || 'user@example.com'}
                  />
                </Animated.View>
              )}

              {REFERRAL_LINK && COPY.referralSettings && (
                <>
                  {/* Referral Program Section */}
                  <SettingsSection title={REFERRAL_LINK.label}>
                    <SettingsItemExpandable
                      icon={<Gift size={20} color={colors.traffic.green} />}
                      label={COPY.referralSettings.label}
                      description={COPY.referralSettings.description}
                      expanded={showReferralProgram}
                      onToggle={() => setShowReferralProgram(!showReferralProgram)}
                      stats={referralStats.totalReferrals > 0 ? {
                        total: referralStats.totalReferrals,
                        converted: referralStats.convertedReferrals,
                      } : undefined}
                    />
                  </SettingsSection>

                  {/* Referral Program (expanded) */}
                  {showReferralProgram && (
                    <Animated.View entering={FadeInDown.duration(400)} className="mb-6 -mt-3">
                      <ReferralCard
                        userId={user?.id || 'user-123'}
                        userType={(userRole === 'admin' || userRole === 'pending' ? 'startup' : userRole) || 'startup'}
                      />
                    </Animated.View>
                  )}
                </>
              )}

              {/* Notifications Section */}
              <SettingsSection title="Notifications">
                <SettingsToggle
                  icon={<Mail size={20} color={colors.primary[600]} />}
                  label="Email Notifications"
                  description="Investor matches, score updates"
                  value={notifications.email}
                  onToggle={(value) => setNotifications({ ...notifications, email: value })}
                />
                <SettingsToggle
                  icon={<Smartphone size={20} color={colors.primary[600]} />}
                  label="Push Notifications"
                  description="Real-time alerts on your device"
                  value={notifications.push}
                  onToggle={(value) => setNotifications({ ...notifications, push: value })}
                />
                <SettingsToggle
                  icon={<Bell size={20} color={colors.primary[600]} />}
                  label="Marketing Emails"
                  description="Tips, product updates, newsletters"
                  value={notifications.marketing}
                  onToggle={(value) => setNotifications({ ...notifications, marketing: value })}
                />
              </SettingsSection>

              {/* Preferences Section */}
              <SettingsSection title="Preferences">
                <SettingsToggle
                  icon={<Moon size={20} color={colors.primary[600]} />}
                  label="Dark Mode"
                  description="Use dark theme"
                  value={isDark}
                  onToggle={toggleDarkMode}
                />
                <SettingsItem
                  icon={<Globe size={20} color={colors.primary[600]} />}
                  label="Language"
                  value="English"
                  onPress={() => showComingSoon('Language settings')}
                />
              </SettingsSection>

              {/* API Keys — visible to investors and admins */}
              {(userRole === 'investor' || userRole === 'admin') && (
                <ApiKeySettings />
              )}

              {/* Data Section */}
              <SettingsSection title="Your Data">
                <SettingsToggle
                  icon={<BarChart3 size={20} color={colors.primary[600]} />}
                  label="Usage analytics"
                  description={analyticsDescription}
                  value={accountOptOut === false}
                  disabled={!deletionAvailable || typeof accountOptOut !== 'boolean' || analyticsOptOut.isPending}
                  onToggle={changeAnalytics}
                />
                <SettingsItem
                  icon={<Download size={20} color={colors.primary[600]} />}
                  label={isExporting ? 'Exporting...' : 'Export Your Data'}
                  description={isExporting ? 'Preparing your data export' : 'Download all your data'}
                  onPress={handleExportData}
                />
                <SettingsItem
                  icon={<SlidersHorizontal size={20} color={colors.primary[600]} />}
                  label="Manage My Data"
                  description={
                    deletionAvailable
                      ? 'Delete specific items without closing your account'
                      : isDemoMode()
                        ? 'Not available on shared demo accounts'
                        : 'Not available in offline sample mode'
                  }
                  onPress={deletionAvailable ? () => router.push('/manage-data') : undefined}
                />
                <SettingsItem
                  icon={<Trash2 size={20} color={colors.error} />}
                  label="Delete Account"
                  description={
                    deletionAvailable
                      ? 'Scheduled for 30 days — cancel any time before'
                      : isDemoMode()
                        ? 'Not available on shared demo accounts'
                        : 'Not available in offline sample mode'
                  }
                  onPress={deletionAvailable ? () => router.push('/delete-account') : undefined}
                  danger
                />
              </SettingsSection>

              {/* Support Section */}
              <SettingsSection title="Support">
                <SettingsItem
                  icon={<HelpCircle size={20} color={colors.primary[600]} />}
                  label="Help & Support"
                  onPress={() => router.push(userRole === 'startup' ? '/(startup)/help' : '/(investor)/help')}
                />
                <SettingsItem
                  icon={<ExternalLink size={20} color={colors.primary[600]} />}
                  label="Terms of Service"
                  onPress={() => router.push('/terms')}
                />
                <SettingsItem
                  icon={<ExternalLink size={20} color={colors.primary[600]} />}
                  label="Privacy Policy"
                  onPress={() => router.push('/privacy')}
                />
                <SettingsItem
                  icon={<ExternalLink size={20} color={colors.primary[600]} />}
                  label="Cookie Policy"
                  onPress={() => router.push('/cookies')}
                />
                <SettingsItem
                  icon={<ExternalLink size={20} color={colors.primary[600]} />}
                  label="Refund Policy"
                  onPress={() => router.push('/refunds')}
                />
                <SettingsItem
                  icon={<ExternalLink size={20} color={colors.primary[600]} />}
                  label="Financial Disclaimer"
                  onPress={() => router.push('/disclaimer')}
                />
              </SettingsSection>

              {/* Developer Section - admin only in prod, always visible in __DEV__ */}
              {devMenuUnlocked && (userRole === 'admin' || __DEV__) && (
                <SettingsSection title="Developer">
                  <View className="px-4 py-4">
                    <View className="flex-row items-center gap-2 mb-3">
                      <Code size={16} color={colors.text.secondary} />
                      <Text className="text-sm font-medium" style={{ color: colors.text.secondary }}>
                        App Mode
                      </Text>
                      <View
                        className="px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: colors.primary[50] }}
                      >
                        <Text className="text-xs font-semibold" style={{ color: colors.primary[700] }}>
                          {modeDescriptions[APP_MODE].label}
                        </Text>
                      </View>
                    </View>
                    <DevModeToggle />
                  </View>
                </SettingsSection>
              )}

              {/* Sign Out */}
              <AnimatedPressable
                onPress={handleSignOut}
                accessibilityRole="button"
                accessibilityLabel="Sign out"
                className="flex-row items-center justify-center gap-2 py-4 rounded-xl mt-4"
                style={{
                  backgroundColor: colors.surface.primary,
                  borderWidth: 1,
                  borderColor: colors.error,
                }}
              >
                <LogOut size={20} color={colors.error} />
                <Text style={{ color: colors.error }} className="font-semibold">
                  Sign Out
                </Text>
              </AnimatedPressable>

              {/* Version - 7-tap to unlock dev menu */}
              {/* Reports what is ACTUALLY running, not a hardcoded string.
                  "embedded bundle" means no OTA has been applied — the app is on
                  the JS compiled into the binary, whatever the server is serving.
                  An update id means one is live and can be matched against
                  `eas update:list`. This was `Inverio v1.0.1`, derived from
                  nothing, which made a non-applying OTA impossible to diagnose
                  from the device. */}
              <Pressable onPress={handleVersionTap} accessibilityRole="text" accessibilityLabel={buildLine}>
                <Text
                  style={{ color: colors.text.tertiary }}
                  className="text-center text-xs mt-6"
                  numberOfLines={2}
                >
                  {buildLine}
                </Text>
              </Pressable>
            </View>
          </ScrollView>
        </SafeAreaView>
      </View>
      <StepUpModal
        visible={analyticsStepUp !== null}
        reason="change your analytics setting"
        onClose={() => setAnalyticsStepUp(null)}
        onVerified={() => {
          const optOut = analyticsStepUp;
          setAnalyticsStepUp(null);
          if (optOut !== null) changeAnalytics(!optOut);
        }}
      />
    </AppLayout>
  );
}

function SettingsSection({ title, children }: { title: string; children: React.ReactNode }) {
  const colors = useThemeColors();
  return (
    <Animated.View entering={FadeInDown.duration(500)} className="mb-6">
      <Text style={{ color: colors.text.secondary }} className="text-sm font-semibold uppercase tracking-wide mb-3 px-1">
        {title}
      </Text>
      <View
        className="rounded-2xl overflow-hidden"
        style={{
          backgroundColor: colors.surface.primary,
          borderWidth: 1,
          borderColor: colors.border.light,
        }}
      >
        {children}
      </View>
    </Animated.View>
  );
}

function SettingsItemExpandable({
  icon,
  label,
  description,
  expanded,
  onToggle,
  stats,
}: {
  icon: React.ReactNode;
  label: string;
  description?: string;
  expanded: boolean;
  onToggle: () => void;
  stats?: { total: number; converted: number };
}) {
  const colors = useThemeColors();
  return (
    <AnimatedPressable
      onPress={onToggle}
      accessibilityRole="button"
      accessibilityLabel={`${label}${description ? `, ${description}` : ''}`}
      accessibilityState={{ expanded }}
      className="flex-row items-center px-4 py-4"
      style={{ borderColor: colors.border.light }}
    >
      <View
        className="w-10 h-10 rounded-xl items-center justify-center mr-3"
        style={{ backgroundColor: `${colors.traffic.green}15` }}
      >
        {icon}
      </View>
      <View className="flex-1">
        <View className="flex-row items-center gap-2">
          <Text style={{ color: colors.text.primary }} className="font-medium">
            {label}
          </Text>
          {stats && stats.total > 0 && (
            <View
              className="flex-row items-center gap-1 px-2 py-0.5 rounded-full"
              style={{ backgroundColor: colors.traffic.greenLight }}
            >
              <TrendingUp size={12} color={colors.traffic.green} />
              <Text style={{ color: colors.traffic.green }} className="text-xs font-semibold">
                {stats.converted}/{stats.total}
              </Text>
            </View>
          )}
        </View>
        {description && (
          <Text style={{ color: colors.text.tertiary }} className="text-sm mt-0.5">
            {description}
          </Text>
        )}
      </View>
      {expanded ? (
        <ChevronUp size={20} color={colors.primary[600]} />
      ) : (
        <ChevronDown size={20} color={colors.text.tertiary} />
      )}
    </AnimatedPressable>
  );
}

function SettingsToggle({
  icon,
  label,
  description,
  value,
  disabled = false,
  onToggle,
}: {
  icon: React.ReactNode;
  label: string;
  description?: string;
  value: boolean;
  /** e.g. while the stored value is still loading */
  disabled?: boolean;
  onToggle: (value: boolean) => void;
}) {
  const colors = useThemeColors();
  return (
    <View className="flex-row items-center px-4 py-4 border-b" style={{ borderColor: colors.border.light }}>
      <View
        className="w-10 h-10 rounded-xl items-center justify-center mr-3"
        style={{ backgroundColor: colors.primary[50] }}
      >
        {icon}
      </View>
      <View className="flex-1 mr-3">
        <Text numberOfLines={1} style={{ color: colors.text.primary }} className="font-medium">
          {label}
        </Text>
        {description && (
          <Text numberOfLines={3} style={{ color: colors.text.tertiary }} className="text-sm mt-0.5">
            {description}
          </Text>
        )}
      </View>
      <Switch
        value={value}
        disabled={disabled}
        onValueChange={onToggle}
        trackColor={{ false: colors.border.medium, true: colors.primary[600] }}
        thumbColor="white"
        accessibilityRole="switch"
        accessibilityLabel={label}
        accessibilityHint={description}
        accessibilityState={{ checked: value, disabled }}
      />
    </View>
  );
}

/** Human size for the export confirmation. */
function formatExportSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
